import React, { useState } from 'react';
import { useContent } from '../hooks/useContent';
import { resolveAssetUrl } from '../lib/api';

const AdvisoryServices = () => {
  const { t } = useContent('advisory');
  const DEFAULT_PHOTO = 'https://customer-assets-rejwkqb3.emergentagent.net/job_stone-temple-preview/artifacts/3kl8q6nm_Screenshot%202026-10-05%20at%2011.26.39%E2%80%AFPM.png';
  const photoUrl = resolveAssetUrl(t('hero.photo_url', '') || DEFAULT_PHOTO);
  return (
    <main className="pt-32 pb-24" data-testid="advisory-services-page">
      {/* Hero — text left, square image right */}
      <section className="max-w-[1440px] mx-auto px-8 md:px-14 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-8">{t('hero.kicker', 'ADVISORY SERVICES')}</p>
            <h1 className="font-serif text-5xl md:text-7xl text-navy leading-[0.95]" data-testid="advisory-heading">
              {t('hero.title_line1', 'Where every dollar')}<br /><span className="italic">{t('hero.title_line2', 'finds its purpose.')}</span>
            </h1>
            <p className="font-serif text-xl md:text-2xl text-navy leading-snug mt-10 max-w-2xl">
              {t('hero.body1', 'Most families are advised in fragments — an investment manager here, an estate attorney there, a philanthropy conversation that happens once a decade if at all. Full Spectrum Advisory treats every financial decision as part of one coherent strategy.')}
            </p>
            <p className="font-serif text-lg text-navy/70 mt-6 max-w-2xl leading-snug">
              {t('hero.body2', 'We act as your quarterback, not just your investment manager — coordinating the specialists in your life so every relationship works toward the same plan.')}
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="relative w-full max-w-md overflow-hidden bg-cream2" data-testid="advisory-visual-box">
              <img
                src={photoUrl}
                alt="Stone Temple Partners founders"
                className="block w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Full Spectrum Advisory */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="advisory-full-spectrum">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('fs.kicker')}</p>
          <h2 className="font-serif text-4xl md:text-6xl text-navy leading-tight max-w-4xl">
            {t('fs.title_line1')}<br /><span className="italic">{t('fs.title_line2')}</span>
          </h2>
          <p className="font-serif text-xl text-navy/70 mt-8 max-w-3xl leading-snug">
            {t('fs.body')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14 border-t border-navy/15 pt-12">
            {(t('fs.blocks') || []).map((b, i) => <FullSpectrumBlock key={i} title={b.title} desc={b.desc} />)}
          </div>
        </div>
      </section>

      {/* Six Domains — hover popups, no numbers/circles */}
      <section className="py-16 md:py-24 border-t border-navy/10" data-testid="advisory-six-domains">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('domains.kicker')}</p>
          <h3 className="font-serif text-4xl md:text-5xl text-navy leading-tight max-w-3xl">
            {t('domains.title')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {(t('domains.items') || []).map((d, i) => (
              <DomainCard key={i} domain={d} hint={t('domains.hint')} />
            ))}
          </div>
        </div>
      </section>

      {/* Process — 5 floating words connected by arrows */}
      <section className="py-16 md:py-28 border-t border-navy/10" data-testid="advisory-process">
        <div className="max-w-[1440px] mx-auto px-8 md:px-14">
          <p className="font-sans text-[11px] tracking-[0.35em] text-navy/60 mb-6">{t('process.kicker')}</p>
          <h3 className="font-serif text-4xl md:text-5xl text-navy leading-tight max-w-3xl">
            {t('process.title')}
          </h3>

          <ProcessRow steps={(t('process.steps') || []).map((st, i) => ({ ...st, key: `${i}-${(st.label || '').toLowerCase()}` }))} />

          <p className="font-serif text-lg md:text-xl text-navy/75 mt-12 max-w-3xl leading-snug" data-testid="process-intro">
            {t('process.body')}
          </p>
        </div>
      </section>
    </main>
  );
};

const FullSpectrumBlock = ({ title, desc }) => (
  <div>
    <p className="font-sans text-[10px] tracking-[0.3em] text-navy/60 mb-4">{(title || '').toUpperCase()}</p>
    <p className="font-serif text-lg text-navy/85 leading-snug">{desc}</p>
  </div>
);

const DomainCard = ({ domain, hint }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="relative border-t border-navy/25 pt-6 min-h-[180px] cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      tabIndex={0}
      data-testid={`domain-card-${(domain.title || '').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <h5 className="font-serif text-2xl md:text-3xl text-navy leading-tight">{domain.title}</h5>
      <p className="font-sans text-sm text-navy/60 italic mt-3 tracking-wide">{hint}</p>

      {/* Hover popup */}
      <div
        className={`absolute left-0 right-0 top-0 z-10 bg-cream shadow-xl border border-navy/15 p-6 transition-all duration-300 ${
          hovered ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        data-testid={`domain-popup-${(domain.title || '').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <h5 className="font-serif text-2xl text-navy leading-tight">{domain.title}</h5>
        <p className="font-sans text-sm text-navy/80 mt-3 leading-relaxed">{domain.desc}</p>
        <p className="font-serif italic text-sm text-navy/60 mt-4 pt-4 border-t border-navy/10 leading-snug">
          {domain.output}
        </p>
      </div>
    </div>
  );
};

const ProcessRow = ({ steps }) => (
  <div className="mt-16" data-testid="process-flow">
    {/* Desktop: horizontal row with arrows */}
    <div className="hidden md:flex items-start justify-between gap-4 relative">
      {steps.map((s, idx) => (
        <React.Fragment key={s.key}>
          <ProcessStep step={s} index={idx} />
          {idx < steps.length - 1 && <ProcessArrow />}
        </React.Fragment>
      ))}
    </div>

    {/* Mobile: vertical stack with down arrows */}
    <div className="md:hidden space-y-8">
      {steps.map((s, idx) => (
        <React.Fragment key={s.key}>
          <ProcessStep step={s} index={idx} vertical mobile />
          {idx < steps.length - 1 && (
            <div className="flex justify-center text-navy/40 text-2xl leading-none">↓</div>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const ProcessStep = ({ step, index, vertical = false, mobile = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`flex-1 ${vertical ? '' : 'text-center'} group cursor-default`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`${mobile ? 'process-step-mobile-' : 'process-step-'}${step.key}`}
    >
      <p className={`font-serif text-2xl md:text-3xl lg:text-4xl transition-all duration-300 ${
        hovered ? 'text-navy italic' : 'text-navy/85'
      } ${vertical ? 'text-left' : ''}`}>
        {step.label}
      </p>
      <div className={`overflow-hidden transition-all duration-500 ${hovered ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
        <p className={`font-sans text-sm text-navy/75 leading-relaxed ${vertical ? 'text-left' : 'max-w-[220px] mx-auto'}`}>
          {step.desc}
        </p>
      </div>
    </div>
  );
};

const ProcessArrow = () => (
  <div className="flex items-center pt-3 text-navy/40" aria-hidden="true">
    <svg width="40" height="10" viewBox="0 0 40 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="0" y1="5" x2="34" y2="5" stroke="currentColor" strokeWidth="1" />
      <path d="M28 1 L34 5 L28 9" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

export default AdvisoryServices;
